import { FinishedRouteAlert, Message } from "@/reducers/messagesSlice"
import { Badge } from "@/components/ui/badge"
import { Check, CircleAlert } from "lucide-react"
import React, { useCallback } from "react"
import { formatDuration } from "@/components/RouteStopView"
import { useAppDispatch } from "@/reducers/hooks"
import { updatePDFViewer } from "@/reducers/overlaysSlice"
import SlickAIGalleryLink from "@/components/chat/SlickAIGalleryLink"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FinishAlertBadgeStats } from "./FinishAlertBadgeStats"
import { useTranslation } from "react-i18next"

export function FinishAlertBade(
  props: FinishedRouteAlert & { message: Message },
) {
  const statsEnabled = useFeatureFlagEnabled("stats")

  if (statsEnabled) {
    return <FinishAlertBadgeStats {...props} />
  }

  return <FinishAlertBadeOriginal {...props} />
}

function FinishAlertBadeOriginal({
  actualDuration,
  expectedDuration,
  actualDistance,
  expectedDistance,
  actualApproachDistance,
  actualApproachDuration,
  expectedTolls,
  actualTolls,
  actualApproachTolls,
  fileName,
  fileUrl,
  message,
}: FinishedRouteAlert & { message: Message }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const onTime = expectedDuration >= actualDuration
  const distanceDifference = actualDistance && actualDistance - expectedDistance
  const durationDifference = actualDuration - expectedDuration

  const distanceText =
    distanceDifference && distanceDifference !== 0
      ? ` (${distanceDifference > 0 ? "+" : "-"}${Math.abs(distanceDifference / 1000).toFixed(0)}km)`
      : ""

  const expectedCostSum =
    expectedTolls?.reduce((sum, toll) => sum + (toll.price || 0), 0) || 0

  const actualCostSum =
    actualTolls?.reduce((sum, toll) => sum + (toll.price || 0), 0) || 0

  const actualApproachCostSum =
    actualApproachTolls?.reduce((sum, toll) => sum + (toll.price || 0), 0) || 0

  const durationText =
    durationDifference !== 0
      ? ` (${durationDifference > 0 ? "+" : "-"}${formatDuration(durationDifference)})`
      : ""

  const openPDFViwer = useCallback(() => {
    dispatch(
      updatePDFViewer({
        visible: true,
        PDFMessage: {
          ...message,
          url: fileUrl,
        },
      }),
    )
  }, [fileUrl, dispatch, message])

  let deliveryLink = null

  if (fileUrl && !/\.pdf$/i.test(fileUrl)) {
    deliveryLink = (
      <span>
        <SlickAIGalleryLink href={fileUrl} messageId={message.id}>
          <span className="text-blue-600 underline hover:text-blue-800">
            {fileName || t("maps.finishAlertBadge.file")}
          </span>
        </SlickAIGalleryLink>
      </span>
    )
  } else if (fileUrl && /\.pdf$/i.test(fileUrl)) {
    deliveryLink = (
      <span>
        <a
          href={fileUrl}
          onClick={(e) => {
            e.preventDefault()
            openPDFViwer()
          }}
        >
          <span className="text-blue-600 underline hover:text-blue-800">
            {fileName || t("maps.finishAlertBadge.file")}
          </span>
        </a>
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-y-2">
      <div>
        <span>{deliveryLink}</span>
      </div>
      <div className="max-w-[500px] overflow-hidden rounded-lg border border-border bg-white shadow-sm dark:bg-gray-900">
        <div className="relative grid grid-cols-4 gap-4 border-b border-border bg-muted/30 px-2 py-4">
          <div>
            <Badge variant={onTime ? "positive" : "error"}>
              {onTime
                ? t("maps.finishAlertBadge.onTime")
                : t("maps.finishAlertBadge.late")}
              {onTime ? (
                <Check className="ml-1 h-3 w-3" />
              ) : (
                <CircleAlert className="ml-1 h-3 w-3" />
              )}
            </Badge>
          </div>
          <div className="text-left">
            <span className="font-medium text-muted-foreground">
              {t("maps.finishAlertBadge.expected")}
            </span>
          </div>
          <div className="text-left">
            <span className="font-medium text-muted-foreground">
              {t("maps.finishAlertBadge.actual")}
            </span>
          </div>
          <div className="text-left">
            <span className="font-medium text-muted-foreground">
              {t("maps.finishAlertBadge.approach")}
            </span>
          </div>
          <div
            className="absolute inset-y-0 border-l border-border"
            style={{ left: "calc(75% - 0.5rem)" }}
          ></div>
        </div>

        <div className="relative grid grid-cols-4 gap-4 border-b border-border px-2 py-4 last:border-b-0">
          <div>
            <span className="text-sm font-medium text-foreground">
              {t("maps.finishAlertBadge.distance")}
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {Math.abs(expectedDistance / 1000).toFixed(0)}km
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {actualDistance && Math.abs(actualDistance / 1000).toFixed(0)}km
            </span>
            {distanceDifference !== 0 && (
              <span
                className={`ml-2 font-mono text-sm ${
                  distanceDifference && distanceDifference > 0
                    ? "text-destructive"
                    : "text-green-600"
                }`}
              >
                {distanceText}
              </span>
            )}
          </div>
          <div className="pr-2 text-left">
            <span className="font-mono text-sm">
              {actualApproachDistance
                ? Math.abs(actualApproachDistance / 1000).toFixed(0) + "km"
                : "-"}
            </span>
          </div>
          <div
            className="absolute inset-y-0 border-l border-border"
            style={{ left: "calc(75% - 0.5rem)" }}
          ></div>
        </div>

        <div className="relative grid grid-cols-4 gap-4 border-b border-border px-2 py-4 last:border-b-0">
          <div>
            <span className="text-sm font-medium text-foreground">
              {t("maps.finishAlertBadge.duration")}
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {formatDuration(expectedDuration)}
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {formatDuration(actualDuration)}
            </span>
            {durationDifference !== 0 && (
              <span
                className={`ml-2 font-mono text-sm ${
                  durationDifference > 0 ? "text-destructive" : "text-green-600"
                }`}
              >
                {durationText}
              </span>
            )}
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {actualApproachDuration
                ? formatDuration(actualApproachDuration)
                : "-"}
            </span>
          </div>
          <div
            className="absolute inset-y-0 border-l border-border"
            style={{ left: "calc(75% - 0.5rem)" }}
          ></div>
        </div>

        <div className="relative grid grid-cols-4 gap-4 border-b border-border px-2 py-4 last:border-b-0">
          <div>
            <span className="text-sm font-medium text-foreground">
              {t("maps.finishAlertBadge.cost")}
            </span>
          </div>

          <div className="text-left">
            <span className="font-mono text-sm">
              {expectedTolls && expectedTolls.length > 0
                ? `${expectedCostSum.toFixed(2)} EUR`
                : "-"}
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {actualTolls && actualTolls.length > 0
                ? `${actualCostSum.toFixed(2)} EUR`
                : "-"}
            </span>
          </div>
          <div className="text-left">
            <span className="font-mono text-sm">
              {actualApproachTolls && actualApproachTolls.length > 0
                ? `${actualApproachCostSum.toFixed(2)} EUR`
                : "-"}
            </span>
          </div>
          <div
            className="absolute inset-y-0 border-l border-border"
            style={{ left: "calc(75% - 0.5rem)" }}
          ></div>
        </div>
      </div>
    </div>
  )
}
